/**
 * CustomerProfileProvider — Customer 360 Sprint (Day 1)
 *
 * One CustomerDetailModal instance, shared across every office surface. Any
 * component nested under this provider can open a customer profile by id:
 *
 *   const { openCustomer } = useCustomerProfile();
 *   openCustomer('cust_abc123');
 *
 * Back-stack: opening B from inside A pushes B on top; closing pops back to A.
 * Capped at 5 deep — additional opens are warn-rejected. CustomerDetailModal's
 * own internal navStack still handles parent/child site drilling within a
 * single record (separate concept).
 *
 * useCustomerProfile() guard: returns a no-op + console.warn when called
 * outside the provider, so tech-side accidental usage fails loud (visible
 * warning) but doesn't crash the whole component tree.
 *
 * Mounted once inside OfficeLayout (nested under GlobalSearchProvider so
 * CustomerLink's name-only fallback can fire global search).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { doc as firestoreDoc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { CustomerDetailModal } from '@/components/CustomerDetailModal';
import type { Customer } from '@/types';

const MAX_STACK_DEPTH = 5;

interface CustomerProfileContextValue {
  /** Fetch the customer doc and open the modal on top of the current view. */
  openCustomer: (customerId: string) => void;
  /** Pop the top customer off the stack. Closes the modal when stack is empty. */
  closeCustomer: () => void;
  /** True when at least one customer is on the stack. */
  isOpen: boolean;
}

const NOTICE_DURATION_MS = 4000;

const CustomerProfileContext = createContext<CustomerProfileContextValue | null>(null);

let outOfProviderWarned = false;
const NOOP_VALUE: CustomerProfileContextValue = {
  openCustomer: (id) => {
    if (!outOfProviderWarned) {
      console.warn(
        '[useCustomerProfile] Called outside <CustomerProfileProvider>. Mount the provider in your layout. openCustomer(' +
          id +
          ') is a no-op.',
      );
      outOfProviderWarned = true;
    }
  },
  closeCustomer: () => {},
  isOpen: false,
};

export function useCustomerProfile(): CustomerProfileContextValue {
  const ctx = useContext(CustomerProfileContext);
  return ctx ?? NOOP_VALUE;
}

interface ProviderProps {
  children: ReactNode;
}

export function CustomerProfileProvider({ children }: ProviderProps) {
  // Back-stack of opened customers. The top is the one shown in the modal.
  const [stack, setStack] = useState<Customer[]>([]);
  // Inline notice shown when the back-stack rejects a push (depth cap) or
  // a fetch fails — so the click isn't silently swallowed.
  const [notice, setNotice] = useState<string | null>(null);
  // Track the auto-dismiss timer so we can clear it on unmount (no
  // setState-on-unmounted warning) AND on consecutive flashes (the new
  // message should reset the timer, not race against a previous one).
  const noticeTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (noticeTimerRef.current !== null) {
        window.clearTimeout(noticeTimerRef.current);
      }
    },
    [],
  );

  const flashNotice = useCallback((msg: string) => {
    setNotice(msg);
    if (noticeTimerRef.current !== null) {
      window.clearTimeout(noticeTimerRef.current);
    }
    noticeTimerRef.current = window.setTimeout(() => {
      setNotice((current) => (current === msg ? null : current));
      noticeTimerRef.current = null;
    }, NOTICE_DURATION_MS);
  }, []);

  const openCustomer = useCallback(
    async (customerId: string) => {
      if (!customerId) return;
      try {
        const snap = await getDoc(firestoreDoc(db, 'customers', customerId));
        if (!snap.exists()) {
          console.warn('[CustomerProfileProvider] customer not found:', customerId);
          flashNotice("Couldn't find that customer record.");
          return;
        }
        const next = { id: snap.id, ...snap.data() } as Customer;
        setStack((prev) => {
          if (prev.length >= MAX_STACK_DEPTH) {
            console.warn(
              '[CustomerProfileProvider] back-stack full (' +
                MAX_STACK_DEPTH +
                '); refusing to push customer ' +
                customerId +
                '. Close one before opening another.',
            );
            flashNotice(
              `Too many customer profiles open (${MAX_STACK_DEPTH}). Close one to open another.`,
            );
            return prev;
          }
          return [...prev, next];
        });
      } catch (err) {
        console.error('[CustomerProfileProvider] openCustomer failed:', err);
        flashNotice("Couldn't open that customer record.");
      }
    },
    [flashNotice],
  );

  const closeCustomer = useCallback(() => {
    setStack((prev) => prev.slice(0, -1));
  }, []);

  const top = stack.length > 0 ? stack[stack.length - 1] : null;

  const value = useMemo<CustomerProfileContextValue>(
    () => ({ openCustomer, closeCustomer, isOpen: stack.length > 0 }),
    [openCustomer, closeCustomer, stack.length],
  );

  return (
    <CustomerProfileContext.Provider value={value}>
      {children}
      <CustomerDetailModal
        isOpen={top !== null}
        onClose={closeCustomer}
        customer={top}
        // z-[400] sits above the default page surface (z-[200]) so cross-
        // entity opens (deal → customer, fault → customer, etc.) render on
        // top. Nested modals INSIDE CustomerDetailModal (JobDetailModal,
        // FaultDetailsView, DealQuickViewModal) were bumped to z-[450]-[460]
        // in the fix-pass to stay above this layer.
        zIndexClass="z-[400]"
      />
      {/* Inline notice — surfaces back-stack-full and fetch-failure cases
          so the user doesn't experience a silent no-op. z-[500] sits above
          every modal so it's always visible when shown. Auto-dismisses
          after NOTICE_DURATION_MS. */}
      {notice && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[500] px-4 py-2.5 rounded-xl bg-surface-card border border-theme-soft shadow-2xl text-sm text-theme-primary max-w-md"
        >
          {notice}
        </div>
      )}
    </CustomerProfileContext.Provider>
  );
}
